import React from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Braces, Check, Clock, Layout, ArrowRight, Briefcase, UserCheck, Target, Lightbulb, MessageCircle, Users, PieChart } from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const ProductManagersUseCase = () => {
  // Benefits data
  const benefits = [
    {
      icon: Clock,
      title: "Accelerate Decision Making",
      description: "Get visual prototypes in minutes instead of days, allowing faster stakeholder alignment and feature validation."
    },
    {
      icon: Target,
      title: "Test Multiple Concepts",
      description: "Quickly generate different approaches to the same problem to identify the most effective solution before committing to development."
    },
    {
      icon: UserCheck,
      title: "Validate Before Building",
      description: "Show interactive prototypes to users and gather feedback before investing engineering resources."
    },
    {
      icon: Lightbulb,
      title: "Communicate Your Vision",
      description: "Bridge the gap between product and design by precisely demonstrating your product ideas to your team."
    }
  ];

  // Use case scenarios
  const scenarios = [
    {
      title: "Feature Definition & Validation",
      description: "Generate multiple versions of a feature to explore options and validate with users before detailed specification."
    },
    {
      title: "Stakeholder Presentations",
      description: "Create interactive prototypes to effectively demonstrate complex user flows during executive reviews."
    },
    {
      title: "Sprint Planning",
      description: "Provide development teams with clear visual references for upcoming sprint work to improve estimation accuracy."
    },
    {
      title: "User Research & Testing",
      description: "Quickly produce testable prototypes to gather user insights and refine requirements before development."
    }
  ];

  // Psychological benefits
  const psychologicalBenefits = [
    {
      icon: MessageCircle,
      title: "Enhanced Cross-Team Communication",
      description: "Transform abstract ideas into concrete visuals that everyone can understand, eliminating misinterpretations and aligning expectations across design, development, and business teams."
    },
    {
      icon: Lightbulb,
      title: "Creative Confidence",
      description: "Explore ideas freely without the constraint of technical implementation concerns, leading to more innovative solutions and breakthrough product concepts."
    },
    {
      icon: Users,
      title: "Stakeholder Trust Building",
      description: "Build credibility and earn trust from executives and stakeholders by presenting well-thought-out interactive experiences rather than static slides or verbal descriptions."
    },
    {
      icon: PieChart,
      title: "Reduced Decision Anxiety",
      description: "Make informed decisions with greater confidence by validating concepts with real interactive prototypes before commitment to development resources."
    }
  ];

  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-green-500 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-bold mb-6">Empower Product Managers</h1>
              <p className="text-xl mb-8 opacity-90">
                Transform product concepts into interactive prototypes without waiting for design resources or technical implementation.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Validate ideas with stakeholders using interactive demos</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Test multiple solutions to the same problem in parallel</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/20 p-1 rounded-full mt-1">
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="ml-3">Accelerate the product development cycle with visual prototypes</p>
                </div>
              </div>
              <Link
                to="/signup"
                className="inline-block px-6 py-3 bg-white text-green-600 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Start Free Trial
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl shadow-2xl">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="bg-white/10 rounded-md p-4">
                  <div className="flex gap-4 mb-3 items-center">
                    <Briefcase className="text-white/70" />
                    <div className="text-sm">Product Concept Visualization</div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white/10 p-2 rounded flex justify-between">
                      <span className="text-xs text-white/70">Feature Adoption</span>
                      <span className="text-xs text-white/90">+28%</span>
                    </div>
                    <div className="bg-white/10 p-2 rounded h-20 flex items-end gap-1">
                      <div className="h-8 w-4 bg-teal-300/50 rounded-t"></div>
                      <div className="h-12 w-4 bg-teal-300/50 rounded-t"></div>
                      <div className="h-10 w-4 bg-teal-300/50 rounded-t"></div>
                      <div className="h-16 w-4 bg-teal-300/50 rounded-t"></div>
                      <div className="h-14 w-4 bg-teal-300/50 rounded-t"></div>
                      <div className="h-12 w-4 bg-teal-300/50 rounded-t"></div>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-xs text-white/70">Visualizing metrics...</div>
                    <LineChart className="text-white/70 h-4 w-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">How Product Managers Use Dessino.ai</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your product management workflow with AI-powered prototyping that helps you communicate ideas, validate concepts, and align stakeholders.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <Braces className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Define & Visualize</h3>
              <p className="text-gray-600">
                Describe your feature in plain language, specifying user flows, key screens and functionality without needing design skills.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <UserCheck className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Validate & Iterate</h3>
              <p className="text-gray-600">
                Show interactive prototypes to stakeholders and users, gather feedback, and rapidly implement changes.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Layout className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Present & Align</h3>
              <p className="text-gray-600">
                Create comprehensive user flows to align teams on the product vision and provide clear direction for development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Product Managers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Benefits for Product Managers</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Accelerate your product development cycle and improve decision-making with visual prototypes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Psychological Benefits Section - ADDED NEW SECTION */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Empower Your Product Vision</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the transformative benefits that come from bringing your ideas to life instantly
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {psychologicalBenefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm flex border border-green-100">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visualization Power Section - ADDED NEW SECTION */}
      <section className="py-20 bg-gradient-to-r from-teal-50 to-green-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">The Power of Visualization</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Turn abstract ideas into tangible experiences that everyone can understand
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-green-700">Before Dessino.ai</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Hours spent explaining concepts in meetings</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Misinterpretations between teams</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Waiting for design resources to visualize ideas</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-red-500 text-xs">✕</span>
                    </div>
                    <p className="text-gray-700">Limited scope for exploration due to resource constraints</p>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold mb-4 text-green-700">With Dessino.ai</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Show instead of tell - immediate visual understanding</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Clear, interactive representations of your exact vision</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Self-sufficiency in creating visual assets</p>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </div>
                    <p className="text-gray-700">Freedom to explore multiple directions simultaneously</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Case Scenarios */}
      <section className="py-20 bg-green-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Practical Use Cases</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how product managers are leveraging Dessino.ai to drive product development
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center mb-3">
                  <Target className="w-5 h-5 text-green-500 mr-2" />
                  <h3 className="text-xl font-bold">{scenario.title}</h3>
                </div>
                <p className="text-gray-600">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-green-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Transform Your Product Development Process</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join product managers who are using Dessino.ai to visualize ideas, align stakeholders, and accelerate development.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/signup" className="px-8 py-3 bg-white text-green-600 rounded-lg hover:bg-gray-100 transition-colors font-medium">
              Start Free Trial
            </Link>
          </div>
          <p className="mt-4 text-sm text-white/80">No credit card required. 10 free generations to get you started.</p>
        </div>
      </section>

      {/* Other Use Cases */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Explore Other Use Cases</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how Dessino.ai helps different roles achieve their goals
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link 
              to="/use-cases/designers"
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">For UX/UI Designers</h3>
              <p className="text-gray-600 mb-4">
                Generate multiple design options quickly and explore creative alternatives without spending hours on each mockup.
              </p>
              <div className="text-green-500 font-medium inline-flex items-center">
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </Link>
            
            <Link 
              to="/use-cases/startups"
              className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-3">For Startup Founders</h3>
              <p className="text-gray-600 mb-4">
                Create impressive prototypes for investor pitches and user testing without hiring a full design team.
              </p>
              <div className="text-green-500 font-medium inline-flex items-center">
                Learn more <ArrowRight className="ml-1 w-4 h-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default ProductManagersUseCase;